import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Col, Label, Input } from "reactstrap";
import TextField from "../../../common/TextField";
import { toast } from "react-toastify";
import { useMutationRequestQuery } from "../../../../Hooks/useMutationRequestQuery";
import { createBuilderService } from "../../../../Services/CRMAdminServices/CRMBuildersService";
import MobileField from "../../../common/MobileField";
import { H3 } from "../../../../AbstractElements";
import DatePicker from "../../../common/DatePicker";
import SelectDatePicker from "../../../common/SelectDatePicker";
import { buildersData } from "../FormData/CrmFormData";
import { useSafeNavigate } from "../../../../Hooks/useSafeNavigate";
import DropdownWithValueName from "../../../common/DropdownWithValueName";
import { useGetRequestQuery } from "../../../../Hooks/useGetRequestQuery";
import { getAllStates, getAllAreas } from "../../../../Services/MMPClientServices/PropertySearchService";

const methodOptions = [

  { value: "BDM email us", label: "BDM email us" },
  { value: "Email BDM", label: "Email BDM" },
  { value: "Manual", label: "Manual" },
  { value: "Portal", label: "Portal" },
];

export default function AddCRMBuilders() {
  const queryClient = useQueryClient();
  const navigate = useSafeNavigate();
  const location = useLocation();
  const isMMPAssetsLiabilitiesRoute = location.pathname === '/MMPAssetsLiabilities';
  interface FormData {
    [key: string]: any;
    statecheckbox: number[];
  }

  const [formData, setFormData] = useState<FormData>({
    ...buildersData,
    method: "", // Method dropdown blank rahe taaki "Select" placeholder dikhe
    statecheckbox: [], // for multi-select
  });
  const [formErrors, setFormErrors] = useState<any>({});
  // const [statesData, setStatesData] = useState<any>([]);
  const [statesData, setStatesData] = useState<{ value: number; label: string }[]>([]);
  const [areasData, setAreasData] = useState<any>([]);
  const [suburb, setSuburb] = useState<any[]>([]);

  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleValueChange = (name: string, value: string | Date | null) => {
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    setFormErrors((prevErrors: any) => ({
      ...prevErrors,
      [name]: "",
    }));
  };

  const {
    data: allStatesData,
    isLoading: allStatesLoading,
    isError: allStatesIsError,
  } = useGetRequestQuery(
    ["getAllStates"],
    getAllStates,
    {
      enabled: true,
    }
  );

  useEffect(() => {
    if (allStatesData?.data && !allStatesIsError && !allStatesLoading) {
      const transformedData = allStatesData.data.map((state: any) => ({
        value: state.id,
        label: state.name
      }));
      setStatesData(transformedData);
    }
  }, [allStatesData, allStatesIsError, allStatesLoading]);

  const {
    data: allAreasData,
    isLoading: allAreasLoading,
    isError: allAreasIsError,
  } = useGetRequestQuery(
    ["getAllAreas", formData.stateId],
    () => getAllAreas({
      searchId: formData.stateId,
      searchType: "area"
    }),
    {
      enabled: !!formData.stateId,
    }
  );

  useEffect(() => {
    if (allAreasData?.data && !allAreasIsError && !allAreasLoading) {
      const transformedData = allAreasData?.data?.map((area: any) => ({
        value: area.id,
        label: area.name
      }));
      setAreasData(transformedData);
    }
  }, [allAreasData, allAreasIsError, allAreasLoading]);

  const {
    data: allSuburbsData,
    isLoading: allSuburbsLoading,
    isError: allSuburbsIsError,
  } = useGetRequestQuery(
    ["getAllSuburbs", formData.areaId],
    () => getAllAreas({
      searchId: formData.areaId,
      searchType: "suburb"
    }),
    {
      enabled: !!formData.areaId,
    }
  );

  useEffect(() => {
    if (allSuburbsData?.data && !allSuburbsIsError && !allSuburbsLoading) {
      const transformedSuburbsData = allSuburbsData?.data?.map((suburb: any) => ({
        value: suburb.id,
        label: suburb.name,
      }));
      setSuburb(transformedSuburbsData);
    }
  }, [allSuburbsData, allSuburbsIsError, allSuburbsLoading]);

  
  const { mutate: addBuilder, isPending: isAdding } = useMutationRequestQuery(
    "addBuilder",
    createBuilderService,
    {
      onSuccess: () => {
        toast.success("Builder added successfully.");
        navigate("/CRMBuilders");
        queryClient.invalidateQueries({ queryKey: ["getAllBuilders"] });
      },
      onError: () => {
        toast.error("Failed to add builder.");
      },
    }
  );

  // All fields mandatory: Company Name, Initial, Method, Followup,
  // BDM First/Last/Mobile/Email, Accounts First/Last/Mobile/Email,
  // State selection, Comments
  const validateForm = () => {
    const errors: any = {};

    if (!formData.supplier || !formData.supplier.toString().trim()) {
      errors.supplier = "Company Name is required.";
    }

    if (!formData.method || formData.method === "Select") {
      errors.method = "Method is required.";
    }

    if (!formData.followUp) {
      errors.followUp = "Followup date is required.";
    }

    if (!formData.bdmMobile || !formData.bdmMobile.toString().trim()) {
      errors.bdmMobile = "BDM Mobile number is required.";
    }

    if (!formData.bdmEmail || !formData.bdmEmail.toString().trim()) {
      errors.bdmEmail = "BDM Email is required.";
    } else if (!isValidEmail(formData.bdmEmail)) {
      errors.bdmEmail = "Enter a valid email address.";
    }

    if (!formData.accMobile || !formData.accMobile.toString().trim()) {
      errors.accMobile = "Accounts Mobile number is required.";
    }

    if (!formData.accEmail || !formData.accEmail.toString().trim()) {
      errors.accEmail = "Accounts Email is required.";
    } else if (!isValidEmail(formData.accEmail)) {
      errors.accEmail = "Enter a valid email address.";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e: any) => {
    if (!validateForm()) {
      toast.error("Please fill all mandatory fields.");
      return;
    }

    const payload = {
      ...formData,
      statecheckbox: Array.isArray(formData.statecheckbox)
        ? formData.statecheckbox.join(',')
        : formData.statecheckbox || '',
    };
    addBuilder(payload);
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: checked, // Ensuring it updates boolean values
    }));
  };

  const handleFileChange = (e: any, fieldName: string) => {
    const file = e.target.files[0];

    if (file) {
      setFormData((prevData) => ({
        ...prevData,
        [fieldName]: file, // Store file directly
      }));
    }
  };

  return (
    <div className={isMMPAssetsLiabilitiesRoute ? 'mmp-assets-liabilities container-fluid' : 'container-fluid'}>
      <div className="table-responsive h-100 py-4">
        <section className='bg-white'>

          <div className="scrollable-table-container">
            <table className="scrollable-table" style={{ marginLeft: '1%', marginTop: '3%' }}>
              <div className="d-flex align-items-center">
                <Col xs={12} className='d-flex'>
                  <H3 className="heading-color f-w-900 text-left my-2">Real Estate Builder</H3>
                </Col>
              </div>
              <div className='d-flex item-center mt-2'>
                <Col className="responsive-col" xs={12} style={{ width: '17%', paddingInline: '2px' }}>
                  <Label className="mb-3">Company Name: <span style={{ color: 'red' }}>*</span></Label>
                </Col>
                <Col className="responsive-col" xs={12} style={{ width: '30%', paddingInline: '2px' }}>
                  <TextField
                    label=""
                    type="text"
                    name="supplier"
                    value={formData.supplier}
                    setValue={handleValueChange}
                    placeholder="Company Name"
                    design="flex"
                    passCol="col-9"
                    maxChars={30}
                  />
                  {formErrors.supplier && (
                    <div style={{ color: 'red', fontSize: '12px', marginTop: '2px' }}>{formErrors.supplier}</div>
                  )}
                </Col>
                <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                  <Label className="mb-3">Initial : </Label>
                </Col>
                <Col className="responsive-col" xs={12} style={{ width: '20%', paddingInline: '2px' }}>
                  <TextField
                    label=""
                    type="text"
                    name="initial"
                    value={formData.initial}
                    setValue={handleValueChange}
                    placeholder="Company Name"
                    design="flex"
                    passCol="col-9"
                    maxChars={20}
                  />
                  {formErrors.initial && (
                    <div style={{ color: 'red', fontSize: '12px', marginTop: '2px' }}>{formErrors.initial}</div>
                  )}
                </Col>
                {/* <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                  <Label className="mb-3"> Type:</Label>
                </Col>
                <Col className="responsive-col" xs={12} style={{ width: '20%', paddingInline: '2px' }}>
                  <DropdownWithValueName
                    label=""
                    name="builderType"
                    value={formData.builderType}
                    setValue={handleValueChange}
                    options={[{value: "Builder", label: "Builder"}, {value: "Real Estate Builder", label: "Real Estate Builder"}]}
                    passCol="col-9"
                    design="flex"
                  />
                </Col> */}
              </div>
              {/* PPT annotation: "New Column - Method" (dropdown list) + "New Column - Followup" (calendar date), added directly below Company Name / Initial row, same layout style */}
              <div className='d-flex item-center mt-2'>
                <Col className="responsive-col" xs={12} style={{ width: '17%', paddingInline: '2px' }}>
                  <Label className="mb-3">Method: <span style={{ color: 'red' }}>*</span></Label>
                </Col>
                <Col className="responsive-col" xs={12} style={{ width: '30%', paddingInline: '2px' }}>
                 
                  <DropdownWithValueName
  label=""
  name="method"
  value={formData.method}
  setValue={handleValueChange}
  options={methodOptions}
  passCol="col-9"
  design="flex"
  placeholder="Select"
/>
                  {formErrors.method && (
                    <div style={{ color: 'red', fontSize: '12px', marginTop: '2px' }}>{formErrors.method}</div>
                  )}
                </Col>
                <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                  <Label className="mb-3">Followup: <span style={{ color: 'red' }}>*</span></Label>
                </Col>
                <Col className="responsive-col" xs={12} style={{ width: '20%', paddingInline: '2px' }}>
                  <SelectDatePicker
                    label=""
                    name="followUp"
                    value={formData.followUp}
                    setValue={handleValueChange}
                    design="flex"
                    passCol="col-9"
                  /> 
                  {formErrors.followUp && (
                    <div style={{ color: 'red', fontSize: '12px', marginTop: '2px' }}>{formErrors.followUp}</div>
                  )}
                </Col>
              </div>
              <div style={{ borderBottom: '3px solid #0000ff' }} className="my-3"></div>
              <div className='d-flex item-center mt-2'>
                <Col className="responsive-col heading-color" xs={12} style={{ width: '11%', paddingInline: '2px' }}>
                  <H3 className="mb-3 f-w-900">Role</H3>
                </Col>
                <Col className="responsive-col heading-color" xs={12} style={{ width: '20%', paddingInline: '2px' }}>
                  <H3 className="mb-3 f-w-900">First Name</H3>
                </Col>
                <Col className="responsive-col heading-color" xs={12} style={{ width: '20%', paddingInline: '2px' }}>
                  <H3 className="mb-3 f-w-900">Surname</H3>
                </Col>
                <Col className="responsive-col heading-color" xs={12} style={{ width: '20%', paddingInline: '2px' }}>
                  <H3 className="mb-3 f-w-900">Mobile</H3>
                </Col>
                <Col className="responsive-col heading-color" xs={12} style={{ width: '20%', paddingInline: '2px' }}>
                  <H3 className="mb-3 f-w-900">Email</H3>
                </Col>
              </div>
              {/* <div className='d-flex item-center '>
                  <Col className="responsive-col " xs={12} style={{ width: '11%', paddingInline: '2px' }}>
                    <Label className="mb-3">Stock List:</Label>
                  </Col>
                  <Col className="responsive-col" xs={12} style={{ width: '20%', paddingInline: '2px' }}>
                    <TextField
                      label=""
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      setValue={handleValueChange}
                      placeholder="First Name"
                      design="flex"
                      passCol="col-9"
                      maxChars={20}
                    />
                  </Col>
                  <Col className="responsive-col" xs={12} style={{ width: '20%', paddingInline: '2px' }}>
                    <TextField
                      label=""
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      setValue={handleValueChange}
                      placeholder="Surname"
                      design="flex"
                      passCol="col-9"
                      maxChars={20}
                    />
                  </Col>
                  <Col className="responsive-col" xs={12} style={{ width: '20%', paddingInline: '2px' }}>
                    <MobileField
                      label=""
                      mobileField="mobile"
                      type="text"
                      value={formData.mobile}
                      setValue={handleValueChange}
                      placeholder="Mobile"
                      design="flex"
                      passCol="col-9"
                      maxLength={14}
                    />
                  </Col>
                  <Col className="responsive-col" xs={12} style={{ width: '20%', paddingInline: '2px' }}>
                    <TextField
                      label=""
                      type="email"
                      name="email"
                      value={formData.email}
                      setValue={handleValueChange}
                      placeholder="Email"
                      design="flex"
                      passCol="col-9"
                      maxChars={50}
                    />
                  </Col>
                </div> */}
              <div className='d-flex item-center '>
                <Col className="responsive-col" xs={12} style={{ width: '11%', paddingInline: '2px' }}>
                  <Label className="mb-3">BDM: <span style={{ color: 'red' }}>*</span></Label>
                </Col>
                <Col className="responsive-col" xs={12} style={{ width: '20%', paddingInline: '2px' }}>
                  <TextField
                    label=""
                    type="text"
                    name="bdmFirstName"
                    value={formData.bdmFirstName}
                    setValue={handleValueChange}
                    placeholder="First Name"
                    design="flex"
                    passCol="col-9"
                    maxChars={20}
                  />
                  {formErrors.bdmFirstName && (
                    <div style={{ color: 'red', fontSize: '12px', marginTop: '2px' }}>{formErrors.bdmFirstName}</div>
                  )}
                </Col>
                <Col className="responsive-col" xs={12} style={{ width: '20%', paddingInline: '2px' }}>
                  <TextField
                    label=""
                    type="text"
                    name="bdmLastName"
                    value={formData.bdmLastName}
                    setValue={handleValueChange}
                    placeholder="Surname"
                    design="flex"
                    passCol="col-9"
                    maxChars={20}
                  />
                  {formErrors.bdmLastName && (
                    <div style={{ color: 'red', fontSize: '12px', marginTop: '2px' }}>{formErrors.bdmLastName}</div>
                  )}
                </Col>
                <Col className="responsive-col" xs={12} style={{ width: '20%', paddingInline: '2px' }}>
                  <MobileField
                    label=""
                    type="text"
                    mobileField="bdmMobile"
                    value={formData.bdmMobile}
                    setValue={handleValueChange}
                    placeholder="Mobile"
                    design="flex"
                    passCol="col-9"
                    maxLength={14}
                  />
                  {formErrors.bdmMobile && (
                    <div style={{ color: 'red', fontSize: '12px', marginTop: '2px' }}>{formErrors.bdmMobile}</div>
                  )}
                </Col>
                <Col className="responsive-col" xs={12} style={{ width: '30%', paddingInline: '2px' }}>
                  <TextField
                    label=""
                    type="email"
                    name="bdmEmail"
                    value={formData.bdmEmail}
                    setValue={handleValueChange}
                    placeholder="Email"
                    design="flex"
                    passCol="col-9"
                    maxChars={50}
                  />
                  {formErrors.bdmEmail && (
                    <div style={{ color: 'red', fontSize: '12px', marginTop: '2px' }}>{formErrors.bdmEmail}</div>
                  )}
                </Col>
              </div>
              <div className='d-flex item-center '>
                <Col className="responsive-col" xs={12} style={{ width: '11%', paddingInline: '2px' }}>
                  <Label className="mb-3">Accounts: <span style={{ color: 'red' }}>*</span></Label>
                </Col>
                <Col className="responsive-col" xs={12} style={{ width: '20%', paddingInline: '2px' }}>
                  <TextField
                    label=""
                    type="text"
                    name="accFirstName"
                    value={formData.accFirstName}
                    setValue={handleValueChange}
                    placeholder="First Name"
                    design="flex"
                    passCol="col-9"
                    maxChars={20}
                  />
                  {formErrors.accFirstName && (
                    <div style={{ color: 'red', fontSize: '12px', marginTop: '2px' }}>{formErrors.accFirstName}</div>
                  )}
                </Col>
                <Col className="responsive-col" xs={12} style={{ width: '20%', paddingInline: '2px' }}>
                  <TextField
                    label=""
                    type="text"
                    name="accLastName"
                    value={formData.accLastName}
                    setValue={handleValueChange}
                    placeholder="Surname"
                    design="flex"
                    passCol="col-9"
                    maxChars={20}
                  />
                  {formErrors.accLastName && (
                    <div style={{ color: 'red', fontSize: '12px', marginTop: '2px' }}>{formErrors.accLastName}</div>
                  )}
                </Col>
                <Col className="responsive-col" xs={12} style={{ width: '20%', paddingInline: '2px' }}>
                  <MobileField
                    label=""
                    type="text"
                    mobileField="accMobile"
                    value={formData.accMobile}
                    setValue={handleValueChange}
                    placeholder="Mobile"
                    design="flex"
                    passCol="col-9"
                    maxLength={14}
                  />
                  {formErrors.accMobile && (
                    <div style={{ color: 'red', fontSize: '12px', marginTop: '2px' }}>{formErrors.accMobile}</div>
                  )}
                </Col>
                <Col className="responsive-col" xs={12} style={{ width: '30%', paddingInline: '2px' }}>
                  <TextField
                    label=""
                    type="email"
                    name="accEmail"
                    value={formData.accEmail}
                    setValue={handleValueChange}
                    placeholder="Email"
                    design="flex"
                    passCol="col-9"
                    maxChars={50}
                  />
                  {formErrors.accEmail && (
                    <div style={{ color: 'red', fontSize: '12px', marginTop: '2px' }}>{formErrors.accEmail}</div>
                  )}
                </Col>
              </div>
              {/* <div className='d-flex item-center '>
                <Col className="responsive-col" xs={12} style={{ width: '11%', paddingInline: '2px' }}>
                  <Label className="mb-3">Construction:</Label>
                </Col>
                <Col className="responsive-col" xs={12} style={{ width: '20%', paddingInline: '2px' }}>
                  <TextField
                    label=""
                    type="text"
                    name="consFirstName"
                    value={formData.consFirstName}
                    setValue={handleValueChange}
                    placeholder="First Name"
                    design="flex"
                    passCol="col-9"
                    maxChars={20}
                  />
                </Col>
                <Col className="responsive-col" xs={12} style={{ width: '20%', paddingInline: '2px' }}>
                  <TextField
                    label=""
                    type="text"
                    name="consLastName"
                    value={formData.consLastName}
                    setValue={handleValueChange}
                    placeholder="Surname"
                    design="flex"
                    passCol="col-9"
                    maxChars={20}
                  />
                </Col>
                <Col className="responsive-col" xs={12} style={{ width: '20%', paddingInline: '2px' }}>
                  <MobileField
                    label=""
                    type="text"
                    mobileField="consMobile"
                    value={formData.consMobile}
                    setValue={handleValueChange}
                    placeholder="Mobile"
                    design="flex"
                    passCol="col-9"
                    maxLength={14}
                  />
                </Col>
                <Col className="responsive-col" xs={12} style={{ width: '20%', paddingInline: '2px' }}>
                  <TextField
                    label=""
                    type="email"
                    name="consEmail"
                    value={formData.consEmail}
                    setValue={handleValueChange}
                    placeholder="Email"
                    design="flex"
                    passCol="col-9"
                    maxChars={50}
                  />
                </Col>
              </div> */}
              <div style={{ borderBottom: '3px solid #0000ff' }} className="my-3"></div>
              <div className="d-flex align-items-center">
                {/* <Col xs={3} className='d-flex' style={{ width: '31%', paddingInline: '2px' }}>
                  <H3 className="heading-color f-w-900 text-left my-2">Stock Portal</H3>
                </Col> */}
                <Col xs={6} className='d-flex'>
                  <H3 className="heading-color f-w-900 text-left my-2">Locations</H3>
                </Col>
              </div>

              <div className="state-checkbox-group mt-2 d-flex align-items-center">
                <Label className="mb-2">State: </Label>
                <div className="checkboxes-container " style={{ display: 'flex', alignItems: "center", gap: '24px', marginBottom:'6px', marginLeft: '8%' }}>
                  {statesData.map((opt: { value: number; label: string }) => (
                    <label key={opt.value} className="circle-checkbox">
                      <input
                        style={{ width: '10px',marginLeft: "10px", height: '10px', borderRadius: 50, border: '2px solid #000' }}
                        type="checkbox"
                        checked={Array.isArray(formData.statecheckbox) && formData.statecheckbox.includes(opt.value)}
                        onChange={() => {
                          setFormData(prev => {
                            const arr = Array.isArray(prev.statecheckbox) ? prev.statecheckbox : [];
                            const updated = arr.includes(opt.value)
                              ? arr.filter(id => id !== opt.value)
                              : [...arr, opt.value];
                            return { ...prev, statecheckbox: updated };
                          });
                          setFormErrors((prevErrors: any) => ({
                            ...prevErrors,
                            statecheckbox: "",
                          }));
                        }}
                      />
                      <span className="custom-circle" />
                      <span className="label-text" style={{ marginLeft: '5px' }}>{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              {formErrors.statecheckbox && (
                <div style={{ color: 'red', fontSize: '12px', marginTop: '2px' }}>{formErrors.statecheckbox}</div>
              )}



              <div className='d-flex item-center mt-2'>
                {/* <Col className="responsive-col" xs={12} style={{ width: '11%', paddingInline: '2px' }}>
                  <Label className="mb-3">Username:</Label>
                </Col>
                <Col className="responsive-col" xs={12} style={{ width: '20%', paddingInline: '2px' }}>
                  <TextField
                    label=""
                    type="text"
                    name="username"
                    value={formData.username}
                    setValue={handleValueChange}
                    placeholder="Enter Username"
                    design="flex"
                    passCol="col-9"
                    maxChars={50}
                  />
                </Col> */}
                {/* <Col className="responsive-col" xs={12} style={{ width: '11%', paddingInline: '2px' }}>
                  <Label className="mb-3">Area:</Label>
                </Col>
                <Col className="responsive-col" xs={12} style={{ width: '20%', paddingInline: '2px' }}>
                  <DropdownWithValueName
                    label=""
                    name="areaId"
                    value={formData.areaId}
                    setValue={handleValueChange}
                    options={areasData}
                    passCol="col-9"
                    design="flex"
                  />
                </Col> */}
              </div>
              <div className='d-flex item-center mt-2'>
                {/* <Col className="responsive-col" xs={12} style={{ width: '11%', paddingInline: '2px' }}>
                  <Label className="mb-3">Password:</Label>
                </Col>
                <Col className="responsive-col" xs={12} style={{ width: '20%', paddingInline: '2px' }}>
                  <TextField
                    label=""
                    type="password"
                    name="password"
                    value={formData.password}
                    setValue={handleValueChange}
                    placeholder="Enter Password"
                    design="flex"
                    passCol="col-9"
                    maxChars={50}
                  />
                </Col> */}
                {/* <Col className="responsive-col" xs={12} style={{ width: '11%', paddingInline: '2px' }}>
                  <Label className="mb-3">Suburb:</Label>
                </Col>
                <Col className="responsive-col" xs={12} style={{ width: '20%', paddingInline: '2px' }}>
                  <DropdownWithValueName
                    label=""
                    name="suburbId"
                    value={formData.suburbId}
                    setValue={handleValueChange}
                    options={suburb}
                    passCol="col-9"
                    design="flex"
                  />
                </Col> */}
              </div>
              <div className='d-flex item-center mt-2'>
                {/* <Col className="responsive-col" xs={12} style={{ width: '11%', paddingInline: '2px' }}>
                  <Label className="mb-3">Updated:</Label>
                </Col>
                <Col className="responsive-col" xs={12} style={{ width: '20%', paddingInline: '2px' }}>
                  <DatePicker
                    label=""
                    name="updateOn"
                    value={formData.updateOn}
                    setValue={handleValueChange}
                    design="flex"
                    passCol="col-9"
                  />
                </Col> */}
              </div>
              <div className='mt-2' style={{ marginBottom: '20px' }}>
                <Col xs={12} style={{ width: '100%', paddingInline: '2px' }}>
                  {/* <Label className="mb-3 heading-color">Comments:</Label> */}
                  <H3 className="heading-color f-w-900 text-left my-2">Comments </H3>

                  <Input
                    type="textarea"
                    name="newComment"
                    value={formData.newComment}
                    onChange={(e) => handleValueChange("newComment", e.target.value)}
                    placeholder="Enter Comments"
                    className="w-75"
                    style={{ height: '100px' }}
                  />
                  {formErrors.newComment && (
                    <div style={{ color: 'red', fontSize: '12px', marginTop: '2px' }}>{formErrors.newComment}</div>
                  )}
                </Col>
              </div>
              {/* <div className="d-md-flex">
                <div style={{ width: '60%' }}>
                  <div className='d-flex item-center mt-2'>
                    <Col className="responsive-col" xs={12} style={{ width: '17%', paddingInline: '2px' }}>
                      <Label className="mb-3 f-w-900">Details</Label>
                    </Col>

                    <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                      <Label className="mb-3 f-w-900">Builds</Label>
                    </Col>

                    <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                      <Label className="mb-3 f-w-900">VIC</Label>
                    </Col>

                    <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                      <Label className="mb-3 f-w-900">QLD-STH</Label>
                    </Col>

                    <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                      <Label className="mb-3 f-w-900">QLD-NTH</Label>
                    </Col>

                    <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                      <Label className="mb-3 f-w-900">NSW</Label>
                    </Col>

                    <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                      <Label className="mb-3 f-w-900">SA</Label>
                    </Col>

                    <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                      <Label className="mb-3 f-w-900">WA</Label>
                    </Col>
                  </div>
                  <div className='d-flex item-center'>
                    <Col className="responsive-col" xs={12} style={{ width: '17%', paddingInline: '2px' }}>
                      <Label className="my-2">Apartment</Label>
                    </Col>

                    <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                      <Input type="checkbox" name="apartment" checked={formData.apartment} onChange={handleCheckboxChange} />
                    </Col>

                    <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                      <Input type="checkbox" name="apartmentVIC" checked={formData.apartmentVIC} onChange={handleCheckboxChange} />
                    </Col>

                    <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                      <Input type="checkbox" name="apartmentQLD" checked={formData.apartmentQLD} onChange={handleCheckboxChange} />
                    </Col>

                    <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                      <Input type="checkbox" name="apartmentQLDNTH" checked={formData.apartmentQLDNTH} onChange={handleCheckboxChange} />
                    </Col>

                    <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                      <Input type="checkbox" name="apartmentNSW" checked={formData.apartmentNSW} onChange={handleCheckboxChange} />
                    </Col>
                    <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                      <Input type="checkbox" name="apartmentSA" checked={formData.apartmentSA} onChange={handleCheckboxChange} />
                    </Col>

                    <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                      <Input type="checkbox" name="apartmentWA" checked={formData.apartmentWA} onChange={handleCheckboxChange} />
                    </Col>
                  </div>

                  <div className='d-flex item-center'>
                    <Col className="responsive-col" xs={12} style={{ width: '17%', paddingInline: '2px' }}>
                      <Label className="my-2">Co-Living</Label>
                    </Col>

                    <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                      <Input type="checkbox" name="co_Living" checked={formData.co_Living} onChange={handleCheckboxChange} />
                    </Col>

                    <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                      <Input type="checkbox" name="colivingVIC" checked={formData.colivingVIC} onChange={handleCheckboxChange} />
                    </Col>

                    <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                      <Input type="checkbox" name="colivingQLD" checked={formData.colivingQLD} onChange={handleCheckboxChange} />
                    </Col>

                    <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                      <Input type="checkbox" name="colivingQLDNTH" checked={formData.colivingQLDNTH} onChange={handleCheckboxChange} />
                    </Col>

                    <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                      <Input type="checkbox" name="colivingNSW" checked={formData.colivingNSW} onChange={handleCheckboxChange} />
                    </Col>

                    <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                      <Input type="checkbox" name="colivingSA" checked={formData.colivingSA} onChange={handleCheckboxChange} />
                    </Col>

                    <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                      <Input type="checkbox" name="colivingWA" checked={formData.colivingWA} onChange={handleCheckboxChange} />
                    </Col>
                  </div>
                  <div className='d-flex item-center'>
                    <Col className="responsive-col" xs={12} style={{ width: '17%', paddingInline: '2px' }}>
                      <Label className="my-2">Dual Income</Label>
                    </Col>

                    <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                      <Input type="checkbox" name="duplex" checked={formData.duplex} onChange={handleCheckboxChange} />
                    </Col>

                    <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                      <Input type="checkbox" name="duplexVIC" checked={formData.duplexVIC} onChange={handleCheckboxChange} />
                    </Col>

                    <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                      <Input type="checkbox" name="duplexQLD" checked={formData.duplexQLD} onChange={handleCheckboxChange} />
                    </Col>

                    <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                      <Input type="checkbox" name="duplexQLDNTH" checked={formData.duplexQLDNTH} onChange={handleCheckboxChange} />
                    </Col>

                    <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                      <Input type="checkbox" name="duplexNSW" checked={formData.duplexNSW} onChange={handleCheckboxChange} />
                    </Col>

                    <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                      <Input type="checkbox" name="duplexSA" checked={formData.duplexSA} onChange={handleCheckboxChange} />
                    </Col>

                    <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                      <Input type="checkbox" name="duplexWA" checked={formData.duplexWA} onChange={handleCheckboxChange} />
                    </Col>
                  </div>
                  <div className='d-flex item-center'>
                    <Col className="responsive-col" xs={12} style={{ width: '17%', paddingInline: '2px' }}>
                      <Label className="my-2">House & Land</Label>
                    </Col>

                    <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                      <Input type="checkbox" name="houseLand" checked={formData.houseLand} onChange={handleCheckboxChange} />
                    </Col>

                    <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                      <Input type="checkbox" name="houseLandVIC" checked={formData.houseLandVIC} onChange={handleCheckboxChange} />
                    </Col>

                    <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                      <Input type="checkbox" name="houseLandQLD" checked={formData.houseLandQLD} onChange={handleCheckboxChange} />
                    </Col>

                    <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                      <Input type="checkbox" name="houseLandQLDNTH" checked={formData.houseLandQLDNTH} onChange={handleCheckboxChange} />
                    </Col>

                    <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                      <Input type="checkbox" name="houseLandNSW" checked={formData.houseLandNSW} onChange={handleCheckboxChange} />
                    </Col>

                    <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                      <Input type="checkbox" name="houseLandSA" checked={formData.houseLandSA} onChange={handleCheckboxChange} />
                    </Col>

                    <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                      <Input type="checkbox" name="houseLandWA" checked={formData.houseLandWA} onChange={handleCheckboxChange} />
                    </Col>
                  </div>
                  <div className='d-flex item-center'>
                    <Col className="responsive-col" xs={12} style={{ width: '17%', paddingInline: '2px' }}>
                      <Label className="my-2">NDIS</Label>
                    </Col>

                    <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                      <Input type="checkbox" name="ndis" checked={formData.ndis} onChange={handleCheckboxChange} />
                    </Col>

                    <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                      <Input type="checkbox" name="ndisVIC" checked={formData.ndisVIC} onChange={handleCheckboxChange} />
                    </Col>

                    <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                      <Input type="checkbox" name="ndisQLD" checked={formData.ndisQLD} onChange={handleCheckboxChange} />
                    </Col>

                    <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                      <Input type="checkbox" name="ndisQLDNTH" checked={formData.ndisQLDNTH} onChange={handleCheckboxChange} />
                    </Col>

                    <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                      <Input type="checkbox" name="ndisNSW" checked={formData.ndisNSW} onChange={handleCheckboxChange} />
                    </Col>

                    <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                      <Input type="checkbox" name="ndisSA" checked={formData.ndisSA} onChange={handleCheckboxChange} />
                    </Col>

                    <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                      <Input type="checkbox" name="ndisWA" checked={formData.ndisWA} onChange={handleCheckboxChange} />
                    </Col>
                  </div>
                  <div className='d-flex item-center'>
                    <Col className="responsive-col" xs={12} style={{ width: '17%', paddingInline: '2px' }}>
                      <Label className="my-2">SMSF</Label>
                    </Col>

                    <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                      <Input type="checkbox" name="landOnly" checked={formData.landOnly} onChange={handleCheckboxChange} />
                    </Col>

                    <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                      <Input type="checkbox" name="landVIC" checked={formData.landVIC} onChange={handleCheckboxChange} />
                    </Col>

                    <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                      <Input type="checkbox" name="landQLD" checked={formData.landQLD} onChange={handleCheckboxChange} />
                    </Col>

                    <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                      <Input type="checkbox" name="landQLDNTH" checked={formData.landQLDNTH} onChange={handleCheckboxChange} />
                    </Col>

                    <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                      <Input type="checkbox" name="landNSW" checked={formData.landNSW} onChange={handleCheckboxChange} />
                    </Col>

                    <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                      <Input type="checkbox" name="landSA" checked={formData.landSA} onChange={handleCheckboxChange} />
                    </Col>

                    <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                      <Input type="checkbox" name="landWA" checked={formData.landWA} onChange={handleCheckboxChange} />
                    </Col>
                  </div>
                  <div className='d-flex item-center'>
                    <Col className="responsive-col" xs={12} style={{ width: '17%', paddingInline: '2px' }}>
                      <Label className="my-2">Townhouse</Label>
                    </Col>

                    <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                      <Input type="checkbox" name="townhouse" checked={formData.townhouse} onChange={handleCheckboxChange} />
                    </Col>

                    <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                      <Input type="checkbox" name="townhouseVIC" checked={formData.townhouseVIC} onChange={handleCheckboxChange} />
                    </Col>

                    <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                      <Input type="checkbox" name="townhouseQLD" checked={formData.townhouseQLD} onChange={handleCheckboxChange} />
                    </Col>

                    <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                      <Input type="checkbox" name="townhouseQLDNTH" checked={formData.townhouseQLDNTH} onChange={handleCheckboxChange} />
                    </Col>

                    <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                      <Input type="checkbox" name="townhouseNSW" checked={formData.townhouseNSW} onChange={handleCheckboxChange} />
                    </Col>

                    <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                      <Input type="checkbox" name="townhouseSA" checked={formData.townhouseSA} onChange={handleCheckboxChange} />
                    </Col>

                    <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                      <Input type="checkbox" name="townhouseWA" checked={formData.townhouseWA} onChange={handleCheckboxChange} />
                    </Col>
                  </div>

                </div>
                <div className="d-flex" style={{ width: '25%' }}>
                   <Col className="" style={{ marginBottom: "20px" }}>
                    <label>Build Facade</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileChange(e, "PropertyImageFile")}
                    />
                    {formData.PropertyImageFile && (
                      <img
                        src={URL.createObjectURL(formData.PropertyImageFile)} // Convert binary to preview URL
                        alt="Property"
                        style={{
                          width: "200px",
                          height: "200px",
                          marginTop: "10px",
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                    )}
                  </Col>

                   <Col className="" style={{ marginBottom: "20px" }}>
                    <label>Stock Report</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileChange(e, "BuildFacadeFile")}
                    />
                    {formData.BuildFacadeFile && (
                      <img
                        src={URL.createObjectURL(formData.BuildFacadeFile)} // Convert binary to preview URL
                        alt="Facade"
                        style={{
                          width: "200px",
                          height: "200px",
                          marginTop: "10px",
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                    )}
                  </Col>
                </div>
              </div> */}
            </table>

            <div className="d-flex gap-3 mt-3 mb-3">
              <Button onClick={() => navigate("/CRMBuilders")} color="secondary">
                Back
              </Button>
              <Button
                onClick={handleSubmit}
                color="primary"
                disabled={isAdding}
                type="submit"
              >
                {isAdding ? "Loading . . ." : "Save"}
              </Button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}